'use client';

/**
 * Ollsoft · Von Rohdaten zum Dokument — "Mensch und Maschine"
 * -----------------------------------------------------------------------------
 * A vertical, portrait process-flow section graphic for the Ollsoft technology
 * page. The chain runs top → down:
 *
 *   Unstrukturierte Daten  →  Strukturierte Daten  →  Analysieren & aufbereiten
 *   →  Entscheiden am Interface (human centrepiece)  →  Dokumente erstellen
 *
 * "Machine" steps are white cards; the single "human decision" step is the
 * tinted blue-grey centrepiece carrying a suggested dashboard (flagged row +
 * navy "Freigeben" pill — the moment of decision).
 *
 * The reveal animation plays once when the graphic scrolls into view, then
 * holds. It uses native CSS transitions + IntersectionObserver and honours
 * `prefers-reduced-motion` (final frame shown immediately, no motion).
 *
 * Drop-in for the Ollsoft Next.js App Router site:
 *
 *   import { OllsoftProcessFlow } from '@/components/OllsoftProcessFlow';
 *   <OllsoftProcessFlow />
 *
 * Requires the Nunito webfont (weights 400/600/700/800 + 400 italic). The site
 * already loads webfonts via tokens/fonts.css; otherwise add Nunito to your
 * <head> (see demo/index.html for the exact <link>).
 */

import { useCallback, useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import './OllsoftProcessFlow.css';

export interface OllsoftProcessFlowProps {
  /** Reveal pace. "Ruhig" = calm/institutional (default), "Zügig" = brisker. */
  pace?: 'Ruhig' | 'Zügig';
  /** Show the italic closing caption. Default true. */
  showCaption?: boolean;
  /** Play automatically on scroll-into-view. Default true. */
  autoplay?: boolean;
  /** Render the title + intro block above the graphic. Default true. */
  showHeader?: boolean;
  /** Header title. */
  title?: string;
  /** Header intro paragraph. */
  intro?: string;
  /** Max rendered width of the (portrait) artboard, in CSS px. Default 600. */
  maxWidth?: number;
  className?: string;
  style?: CSSProperties;
}

const ARTBOARD_W = 1080;

const UNSTRUCTURED = [
  'Gescannte Dokumente',
  'PDF',
  'PDF-Berichte',
  'Sprachaufnahmen',
  'Videoaufnahmen',
  'E-Mail-Verläufe',
];
const STRUCTURED = ['FHIR', 'JSON', 'XML', 'Branchenspezifische Schemata'];

/** A downward connector arrow (drawn top→down on reveal). */
function Arrow({ style }: { style: CSSProperties }) {
  return (
    <svg
      className="opf-arrow opf-anim"
      data-anim="draw"
      style={style}
      width="26"
      height="48"
      viewBox="0 0 26 48"
      fill="none"
      aria-hidden="true"
    >
      <line x1="13" y1="2" x2="13" y2="36" stroke="#88A9C3" strokeWidth="2" />
      <path
        d="M7 30 L13 40 L19 30"
        stroke="#88A9C3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

export function OllsoftProcessFlow({
  pace = 'Ruhig',
  showCaption = true,
  autoplay = true,
  showHeader = true,
  title = 'Ollsoft · Von Rohdaten zum Dokument',
  intro = 'Von unstrukturierten Rohdaten bis zum fertigen Dokument: die Maschine bereitet vor, Ihre Leute entscheiden.',
  maxWidth = 600,
  className,
  style,
}: OllsoftProcessFlowProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const playedRef = useRef(false);

  // pace factor — mirrors the prototype (Zügig = 0.62× the durations/delays).
  const P = pace === 'Zügig' ? 0.62 : 1;

  /** Build the inline CSS vars for one tween (delay + duration, scaled by pace). */
  const a = useCallback(
    (delay: number, dur: number, extra?: Record<string, string>): CSSProperties =>
      ({
        ['--delay' as string]: `${Math.round(delay * P)}ms`,
        ['--dur' as string]: `${Math.round(dur * P)}ms`,
        ...extra,
      }) as CSSProperties,
    [P],
  );

  /** Scale the fixed 1080px artboard to fit the available width (capped). */
  const fit = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const stage = root.querySelector<HTMLElement>('.opf-stage');
    const wrap = root.querySelector<HTMLElement>('.opf-wrap');
    const artboard = root.querySelector<HTMLElement>('.opf-artboard');
    if (!stage || !wrap || !artboard) return;

    const horizontalPad = 80; // .opf left+right padding (40 + 40)
    const avail = Math.min(Math.max(root.clientWidth - horizontalPad, 0), maxWidth);
    const scale = avail / ARTBOARD_W;
    stage.style.width = `${avail}px`;
    artboard.style.transform = `scale(${scale})`;
    wrap.style.height = `${Math.round(artboard.offsetHeight * scale)}px`;
  }, [maxWidth]);

  const reveal = useCallback(() => {
    rootRef.current?.classList.add('is-revealed');
  }, []);

  const replay = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    root.classList.remove('is-revealed');
    // force reflow so the transitions/animation restart from their hidden state
    void root.offsetWidth;
    root.classList.add('is-revealed');
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    fit();
    const reFit = setTimeout(fit, 300); // re-measure once the webfont has loaded
    const ro = new ResizeObserver(() => fit());
    ro.observe(root);

    let io: IntersectionObserver | undefined;
    if (autoplay) {
      const stage = root.querySelector<HTMLElement>('.opf-stage');
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting && !playedRef.current) {
              playedRef.current = true;
              reveal();
            }
          });
        },
        { threshold: 0.25 },
      );
      if (stage) io.observe(stage);
    }

    return () => {
      clearTimeout(reFit);
      ro.disconnect();
      io?.disconnect();
    };
  }, [autoplay, fit, reveal]);

  return (
    <div ref={rootRef} className={`opf${className ? ` ${className}` : ''}`} style={style}>
      {showHeader && (
        <div className="opf-header">
          <div className="opf-title">{title}</div>
          {intro && <div className="opf-intro">{intro}</div>}
        </div>
      )}

      <div className="opf-stage">
        <div className="opf-controls">
          <button type="button" className="opf-replay" onClick={replay}>
            ↻ Abspielen
          </button>
        </div>

        <div className="opf-wrap">
          <section className="opf-artboard">
            <div className="opf-pad">
              {/* LEGEND */}
              <div className="opf-legend opf-anim" data-anim="up8" style={a(140, 420)}>
                <div className="opf-legend-item">
                  <span className="opf-swatch opf-swatch--machine" />
                  <span className="opf-legend-label">Maschine</span>
                </div>
                <div className="opf-legend-item">
                  <span className="opf-swatch opf-swatch--human" />
                  <span className="opf-legend-label">Menschliche Entscheidung</span>
                </div>
              </div>

              {/* CHAIN */}
              <div className="opf-chain">
                {/* Card 0 — unstructured input */}
                <div className="opf-card opf-card--machine opf-anim" data-anim="up18" style={a(440, 600)}>
                  <div className="opf-card-title">Unstrukturierte Daten</div>
                  <div className="opf-chips">
                    {UNSTRUCTURED.map((label, i) => (
                      <span
                        key={label}
                        className="opf-chip opf-anim"
                        data-anim="up6"
                        style={a(690 + i * 60, 320)}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>

                <Arrow style={a(1410, 420)} />

                {/* Card 1 — structured data */}
                <div className="opf-card opf-card--machine opf-anim" data-anim="up18" style={a(1760, 560)}>
                  <div className="opf-card-title">Strukturierte Daten</div>
                  <div className="opf-chips">
                    {STRUCTURED.map((label, i) => (
                      <span
                        key={label}
                        className="opf-chip opf-anim"
                        data-anim="up6"
                        style={a(2010 + i * 60, 320)}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>

                <Arrow style={a(2550, 420)} />

                {/* Card 2 — analyse & prepare */}
                <div className="opf-card opf-card--machine opf-anim" data-anim="up18" style={a(2900, 560)}>
                  <div className="opf-card-title">Analysieren &amp; aufbereiten</div>
                  <div className="opf-card-sub">Entscheidungsgrundlage entsteht</div>
                </div>

                <Arrow style={a(3330, 420)} />

                {/* Card 3 — HUMAN centrepiece */}
                <div className="opf-card opf-card--human opf-anim" data-anim="popCard" style={a(3680, 660)}>
                  <div className="opf-card-title">Entscheiden am Interface</div>
                  <div className="opf-card-sub">Ihre Leute sehen, worauf es ankommt</div>

                  {/* suggested interface panel */}
                  <div className="opf-ui">
                    <div className="opf-ui-dots opf-anim" data-anim="opa" style={a(4060, 300)}>
                      <span className="opf-ui-dot" />
                      <span className="opf-ui-dot" />
                      <span className="opf-ui-dot" />
                    </div>

                    <div className="opf-ui-rows">
                      <div className="opf-ui-row opf-anim" data-anim="up8" style={a(4260, 360)}>
                        <div className="opf-bar" style={{ width: '38%', background: 'rgba(43,66,87,0.10)' }} />
                        <div className="opf-bar" style={{ width: '22%', background: 'rgba(43,66,87,0.07)' }} />
                      </div>

                      <div className="opf-ui-flagrow opf-anim" data-anim="up8" style={a(4430, 420)}>
                        <div className="opf-bar" style={{ width: '34%', background: 'rgba(43,66,87,0.16)' }} />
                        <div className="opf-tag opf-anim" data-anim="popTag" data-ease="back" style={a(4650, 380)}>
                          <span className="opf-tag-dot" />
                          <span className="opf-tag-label">Auffällig</span>
                        </div>
                      </div>

                      <div className="opf-ui-row opf-anim" data-anim="up8" style={a(4850, 360)}>
                        <div className="opf-bar" style={{ width: '30%', background: 'rgba(43,66,87,0.10)' }} />
                        <div className="opf-bar" style={{ width: '26%', background: 'rgba(43,66,87,0.07)' }} />
                      </div>
                    </div>

                    <div className="opf-ui-actions">
                      <div
                        className="opf-btn opf-anim"
                        data-anim="popBtn"
                        data-ease="back"
                        style={a(5130, 520, {
                          ['--glow-delay' as string]: `${Math.round(5260 * P)}ms`,
                          ['--glow-dur' as string]: `${Math.round(1150 * P)}ms`,
                        })}
                      >
                        <span>Freigeben</span>
                        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true">
                          <path
                            d="M3 8.5 L13 8.5 M9 4.5 L13 8.5 L9 12.5"
                            stroke="#ffffff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <Arrow style={a(5790, 420)} />

                {/* Card 4 — output / documents */}
                <div className="opf-card opf-card--machine opf-anim" data-anim="up18" style={a(6140, 560)}>
                  <div className="opf-card-title">Dokumente erstellen</div>
                  <div className="opf-card-sub">fließen in KIS, CRM, ERP</div>
                </div>
              </div>

              {/* CAPTION */}
              {showCaption && (
                <div className="opf-caption opf-anim" data-anim="up10" style={a(6610, 580)}>
                  <div className="opf-caption-text">
                    Die KI bereitet vor und setzt um. Ihre Leute entscheiden.
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default OllsoftProcessFlow;
